import {openDialog, openDeleteDialog, showNotification} from 'platform/components';
import {Box} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {isNil, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {metadaApi} from '@dms/api/base';
import {
  PatchReceiveNoteCorrectionItemsDeleteRequest,
  ReceiveNoteCorrectionTotalPrice,
  useDeleteReceiveNoteCorrectionItemsMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  handleApiError,
  logError,
  refreshDmsDatagrid,
} from '@dms/shared';
import {OverviewTotalPrice, useWarehouseParams} from '@dms/warehouse/shared-components';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {ReceiveNoteCorrectionItemEdit} from './ReceiveNoteCorrectionItemEdit';

interface ListOfItemsTabProps extends RequiredTestIdProps {
  receiveNoteId: string;
  isCreatingReceiveNoteCorrection: boolean;
  isReceiveNoteCorrectionPending: boolean;
  totalPrice: ReceiveNoteCorrectionTotalPrice | Nullish;
  totalPriceCurrency: string;
}

export function ListOfItemsTab(props: ListOfItemsTabProps) {
  const {receiveNoteCorrectionId} = useWarehouseParams();

  const dispatch = useDispatch();

  const formatCurrency = useFormatCurrency();

  // The item edit/delete mutations only invalidate the basket and header actions,
  // not the correction itself, so the Total purchase price under the grid would
  // otherwise stay stale until a manual refresh. Invalidate it manually.
  const invalidateTotalPrice = () =>
    dispatch(
      metadaApi.util.invalidateTags([{type: 'receiveNoteCorrections', id: receiveNoteCorrectionId}])
    );

  const [deleteReceiveNoteCorrectionItems] = useDeleteReceiveNoteCorrectionItemsMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId}) => {
    match(actionKey)
      .with('redirectDetail', () => {
        const receiveNoteCorrectionItemId = isArray(rowId) ? rowId[0] : rowId;

        if (isNil(receiveNoteCorrectionId)) {
          return logError('receiveNoteCorrectionId is not defined');
        }

        if (isNil(receiveNoteCorrectionItemId)) {
          return logError('receiveNoteCorrectionItemId is not defined');
        }

        openDialog(
          <ReceiveNoteCorrectionItemEdit
            receiveNoteCorrectionId={receiveNoteCorrectionId}
            receiveNoteCorrectionItemId={receiveNoteCorrectionItemId}
            isReceiveNoteCorrectionCompleted={!props.isReceiveNoteCorrectionPending}
            onAfterSubmit={() => {
              refreshDmsDatagrid(WAREHOUSE_GRID_CODES.warehouseCreditNoteItem, {deselectAll: true});
              invalidateTotalPrice();
            }}
            data-testid={suffixTestId('itemEditDialog', props)}
          />,
          {
            id: 'itemEditDialog',
            title: i18n.t('entity.warehouse.labels.editReceiveNoteCorrectionItem'),
            size: 'large',
            withAdditionalFooter: true,
            'data-testid': suffixTestId('itemEditDialog', props),
          }
        );
      })
      .with('delete', () => {
        const ids = isArray(rowId) ? rowId : [rowId];

        const requestBody: PatchReceiveNoteCorrectionItemsDeleteRequest['body'] = {
          state: 'ADDED',
          creditNoteItemId: ids,
        };

        openDeleteDialog({
          id: 'itemDeleteDialog',
          onConfirm: async () =>
            await deleteReceiveNoteCorrectionItems({
              creditNoteId: receiveNoteCorrectionId,
              body: requestBody,
            })
              .unwrap()
              .then(() =>
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'))
              )
              .then(() =>
                refreshDmsDatagrid(WAREHOUSE_GRID_CODES.warehouseCreditNoteItem, {
                  deselectAll: true,
                })
              )
              .then(invalidateTotalPrice)
              .catch(handleApiError),
          'data-testid': suffixTestId('itemDeleteDialog', props),
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {creditNoteId: receiveNoteCorrectionId}]),
    [receiveNoteCorrectionId]
  );

  const totalPriceWithoutVat = formatCurrency(
    props.totalPrice?.totalPriceWithoutVat ?? 0,
    props.totalPriceCurrency,
    2
  );

  const totalPriceWithVat = formatCurrency(
    props.totalPrice?.totalPriceWithVat ?? 0,
    props.totalPriceCurrency,
    2
  );

  return (
    <Box height="100%">
      <Box height="95%">
        <DmsDataGrid
          gridCode={WAREHOUSE_GRID_CODES.warehouseCreditNoteItem}
          actionCallback={actionCallback}
          queryModifier={queryModifier}
          data-testid={suffixTestId('receiveNoteCorrectionItems', props)}
        />
      </Box>
      <OverviewTotalPrice
        totalPriceWithoutVat={totalPriceWithoutVat}
        totalPriceWithVat={totalPriceWithVat}
        data-testid={suffixTestId('totalPrice', props)}
      />
    </Box>
  );
}
